(function giftIntegrityModule(root, factory) {
  const api = factory();

  if (typeof module === 'object' && module.exports) {
    module.exports = api;
    return;
  }

  root.RMGiftIntegrity = api;
  api.install();
})(typeof window !== 'undefined' ? window : globalThis, function createGiftIntegrity() {
  'use strict';

  const GIFT_PROPERTY = '_Gifted';
  const ROOT_SELECTOR = '[data-gift-integrity-root]';
  const CHECKOUT_SELECTOR = '[data-gift-integrity-checkout]';
  const REPAIR_SELECTOR = '[data-gift-integrity-repair]';
  const ACCELERATED_SELECTOR = '.additional-checkout-buttons';
  const bypassForms = new WeakSet();
  let checkoutLocked = false;

  const integer = (value) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? Math.max(0, Math.round(parsed)) : 0;
  };

  const isGifted = (item) => {
    const properties = item && item.properties;
    if (!properties || typeof properties !== 'object') return false;
    const marker = properties[GIFT_PROPERTY];
    return marker !== undefined && marker !== null && String(marker).trim() !== '';
  };

  const allocatedAmount = (item) => {
    const allocations = Array.isArray(item.line_level_discount_allocations)
      ? item.line_level_discount_allocations
      : [];
    return allocations.reduce((total, allocation) => {
      return total + integer(allocation && allocation.amount);
    }, 0);
  };

  const analyzeCart = (cart) => {
    const items = Array.isArray(cart && cart.items) ? cart.items : [];
    const gifted = items.filter(isGifted);
    const invalid = gifted.filter((item) => {
      const original = integer(item.original_line_price);
      const final = integer(item.final_line_price);
      return final !== 0 || allocatedAmount(item) < original;
    });

    return {
      valid: invalid.length === 0,
      giftedCount: gifted.length,
      validGiftedCount: gifted.length - invalid.length,
      invalidCount: invalid.length,
      invalidKeys: invalid.map((item) => item.key).filter(Boolean),
    };
  };

  const removalPayload = (keys) => ({
    updates: [...new Set(keys)].reduce((updates, key) => {
      if (key) updates[key] = 0;
      return updates;
    }, {}),
  });

  const config = () => {
    const element = document.querySelector(ROOT_SELECTOR);
    return {
      element,
      cartUrl: element?.dataset.giftIntegrityCartUrl || '/cart.js',
      updateUrl: element?.dataset.giftIntegrityUpdateUrl || '/cart/update.js',
      checking: element?.dataset.giftIntegrityChecking || 'Checking your gift...',
      blocked:
        element?.dataset.giftIntegrityBlocked ||
        'Your gift discount could not be confirmed. Checkout has been paused.',
      removed:
        element?.dataset.giftIntegrityRemoved ||
        'The unconfirmed gift was removed. Please select it again before checkout.',
    };
  };

  const requestJson = async (url, options = {}) => {
    const response = await fetch(url, {
      credentials: 'same-origin',
      cache: 'no-store',
      headers: {
        Accept: 'application/json',
        ...(options.body ? { 'Content-Type': 'application/json' } : {}),
      },
      ...options,
    });
    if (!response.ok) throw new Error(`Cart request failed: ${response.status}`);
    return response.json();
  };

  const fetchCart = () => {
    const { cartUrl } = config();
    const separator = cartUrl.includes('?') ? '&' : '?';
    return requestJson(`${cartUrl}${separator}_=${Date.now()}`);
  };

  const refreshCartDiscounts = async () => {
    const { updateUrl } = config();
    await requestJson(updateUrl, {
      method: 'POST',
      body: JSON.stringify({ updates: {} }),
    });
    await new Promise((resolve) => window.setTimeout(resolve, 300));
    return fetchCart();
  };

  const removeInvalidGifts = async (keys) => {
    if (!keys.length) return;
    const { updateUrl } = config();
    await requestJson(updateUrl, {
      method: 'POST',
      body: JSON.stringify(removalPayload(keys)),
    });
  };

  const messageNode = () => document.getElementById('cart-errors');

  const announce = (message) => {
    const node = messageNode();
    if (node) node.textContent = message;
    const screenreader = document.getElementById('screenreader-announce');
    if (screenreader) screenreader.textContent = message;
  };

  const capture = (event, properties) => {
    if (window.posthog && typeof window.posthog.capture === 'function') {
      window.posthog.capture(event, properties);
    }
  };

  const setDomState = (state, analysis) => {
    document.querySelectorAll(ROOT_SELECTOR).forEach((element) => {
      element.dataset.giftIntegrityState = state;
      element.dataset.giftedCount = String(analysis?.giftedCount || 0);
      element.dataset.invalidGiftCount = String(analysis?.invalidCount || 0);
    });

    const invalid = state === 'invalid' || state === 'error';
    document.querySelectorAll(CHECKOUT_SELECTOR).forEach((button) => {
      button.setAttribute('aria-disabled', String(invalid));
      button.toggleAttribute('disabled', invalid);
    });
    document.querySelectorAll(ACCELERATED_SELECTOR).forEach((container) => {
      const hasGift = (analysis?.giftedCount || 0) > 0;
      container.toggleAttribute('hidden', invalid || hasGift);
      container.toggleAttribute('inert', invalid || hasGift);
    });
  };

  const refreshSections = () => {
    return new Promise((resolve) => {
      let settled = false;
      const finish = () => {
        if (settled) return;
        settled = true;
        resolve();
      };
      document.addEventListener('shapes:cart:unlock', finish, { once: true });
      document.dispatchEvent(new CustomEvent('theme:update:cart'));
      window.setTimeout(finish, 4000);
    });
  };

  const submitCheckout = (form, submitter) => {
    if (!(form instanceof HTMLFormElement)) {
      throw new Error('Cart form is unavailable');
    }

    let checkoutSubmitter =
      submitter instanceof HTMLElement &&
      submitter.matches('button[name="checkout"], input[name="checkout"]')
        ? submitter
        : null;
    let temporarySubmitter = null;

    if (!checkoutSubmitter) {
      temporarySubmitter = document.createElement('button');
      temporarySubmitter.type = 'submit';
      temporarySubmitter.name = 'checkout';
      temporarySubmitter.hidden = true;
      form.appendChild(temporarySubmitter);
      checkoutSubmitter = temporarySubmitter;
    }

    bypassForms.add(form);
    form.requestSubmit(checkoutSubmitter);
    temporarySubmitter?.remove();
  };

  const reconcile = async ({ removeInvalid = false } = {}) => {
    let analysis = analyzeCart(await fetchCart());
    if (analysis.valid) {
      setDomState('valid', analysis);
      return analysis;
    }

    analysis = analyzeCart(await refreshCartDiscounts());
    if (analysis.valid) {
      setDomState('valid', analysis);
      await refreshSections();
      return analysis;
    }

    setDomState('invalid', analysis);
    capture('gift_integrity_blocked', {
      gifted_count: analysis.giftedCount,
      invalid_gift_count: analysis.invalidCount,
    });

    if (removeInvalid) {
      await removeInvalidGifts(analysis.invalidKeys);
      capture('gift_integrity_removed', {
        removed_gift_count: analysis.invalidCount,
      });
      await refreshSections();
    }
    return analysis;
  };

  const guardCheckout = async (event) => {
    if (bypassForms.has(event.target)) {
      bypassForms.delete(event.target);
      return;
    }

    if (checkoutLocked) {
      event.preventDefault();
      return;
    }

    const submitter = event.submitter;
    const cartForm = event.target instanceof HTMLFormElement && event.target.id === 'cart';
    if (!cartForm) return;
    if (submitter && !submitter.matches(CHECKOUT_SELECTOR)) return;

    event.preventDefault();
    checkoutLocked = true;
    const copy = config();
    announce(copy.checking);

    try {
      const analysis = await reconcile({ removeInvalid: true });
      if (analysis.valid) {
        submitCheckout(event.target, submitter);
        return;
      }
      announce(copy.removed);
    } catch (_error) {
      setDomState('error');
      announce(copy.blocked);
      capture('gift_integrity_error', { stage: 'checkout' });
    } finally {
      checkoutLocked = false;
    }
  };

  const repairInvalidGift = async (event) => {
    const button = event.target.closest(REPAIR_SELECTOR);
    if (!button || checkoutLocked) return;
    event.preventDefault();
    checkoutLocked = true;
    const copy = config();
    announce(copy.checking);

    try {
      const analysis = await reconcile({ removeInvalid: true });
      if (analysis.valid) {
        submitCheckout(document.getElementById('cart'));
        return;
      }
      announce(copy.removed);
    } catch (_error) {
      setDomState('error');
      announce(copy.blocked);
      capture('gift_integrity_error', { stage: 'repair' });
    } finally {
      checkoutLocked = false;
    }
  };

  const checkVisibleCart = () => {
    if (!document.querySelector(ROOT_SELECTOR)) return;
    reconcile().catch(() => setDomState('error'));
  };

  const install = () => {
    if (typeof document === 'undefined') return;
    document.addEventListener('submit', guardCheckout, true);
    document.addEventListener('click', repairInvalidGift, true);
    document.addEventListener('shapes:cart:update', () => {
      window.requestAnimationFrame(checkVisibleCart);
    });
    document.addEventListener('theme:cart-drawer:open', checkVisibleCart);
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', checkVisibleCart, { once: true });
    } else {
      checkVisibleCart();
    }
  };

  return {
    GIFT_PROPERTY,
    allocatedAmount,
    analyzeCart,
    isGifted,
    removalPayload,
    install,
  };
});
